import { immerable } from 'immer';
import * as z from 'zod';

/**
 * AppendOnlyArray encapsulates an array and allows (as near as possible) no
 * mutation of the array or the objects inside the array (other than appending
 * new items to the array).
 *
 * This property should hold true inside an immer callback as well.
 */
export class AppendOnlyArray<T extends object> {
  static [immerable] = true;

  private readonly array: Readonly<T>[];

  /**
   * Prevents indexed access and assignment. Use at() method to read values.
   */
  readonly [index: number]: never;

  constructor(array: T[] | readonly T[]) {
    this.array = array.map((item) => Object.freeze({ ...item }));
    // Use Proxy to intercept property access: prevent indexed reads and writes
    // eslint-disable-next-line no-constructor-return
    return new Proxy(this, {
      get(target, prop) {
        // Allow access to all non-numeric properties (methods, length, etc.)
        if (typeof prop !== 'string' || !/^\d+$/.test(prop)) {
          return (target as Record<string | symbol, unknown>)[prop];
        }
        // Prevent indexed reads - throw error (use at() method instead)
        throw new Error(`Indexed access is not allowed. Use at(${prop}) method instead.`);
      },
      set(target, prop, value) {
        // Prevent indexed assignment - throw specific error
        if (typeof prop === 'string' && /^\d+$/.test(prop)) {
          throw new Error(`Indexed assignment is not allowed. Use append() or push() methods instead.`);
        }
        // Allow setting non-numeric properties (shouldn't happen in normal usage)
        return Reflect.set(target, prop, value);
      },
    }) as AppendOnlyArray<T>;
  }

  get length(): number {
    return this.array.length;
  }

  at(index: number): Readonly<T> | undefined {
    const item = this.array.at(index);
    if (!item) return undefined;
    // Always return a freshly frozen clone to prevent mutation even inside Immer drafts.
    return Object.freeze({ ...(item as T) });
  }

  push(...items: T[]): void {
    for (const item of items) {
      this.array.push(Object.freeze({ ...item }));
    }
  }

  toMutableArray(): T[] {
    return [...this.array].map((item) => ({ ...item }));
  }

  /**
   * Appends multiple items to the array in order.
   */
  append(...items: T[]): void {
    for (const item of items) this.push(item);
  }

  /**
   * Appends all items from an iterable source.
   */
  appendMany(items: Iterable<T>): void {
    for (const item of items) this.push(item);
  }

  /**
   * Returns a new AppendOnlyArray with the provided items appended; original is unchanged.
   */
  appended(...items: T[]): AppendOnlyArray<T> {
    return new AppendOnlyArray<T>([...this.toReadonlyArray(), ...items]);
  }

  /**
   * Returns a new AppendOnlyArray containing a shallow copy of a portion of the original array.
   * Original array is unchanged.
   */
  slice(start?: number, end?: number): AppendOnlyArray<T> {
    const mutableArray = this.toReadonlyArray();
    const sliced = mutableArray.slice(start, end);
    return new AppendOnlyArray<T>(sliced);
  }

  /**
   * Enables for..of and spread to iterate frozen items.
   */
  [Symbol.iterator](): IterableIterator<Readonly<T>> {
    const self = this;
    function* iterator(): IterableIterator<Readonly<T>> {
      for (let i = 0; i < self.array.length; i++) {
        yield Object.freeze({ ...(self.array[i] as T) });
      }
    }
    return iterator();
  }

  /**
   * Returns an iterator over values.
   */
  values(): IterableIterator<Readonly<T>> {
    return this[Symbol.iterator]();
  }

  /**
   * Returns an iterator over index, value pairs.
   */
  entries(): IterableIterator<[number, Readonly<T>]> {
    const self = this;
    function* iterator(): IterableIterator<[number, Readonly<T>]> {
      for (let i = 0; i < self.array.length; i++) {
        yield [i, Object.freeze({ ...(self.array[i] as T) })];
      }
    }
    return iterator();
  }

  /**
   * Returns an iterator over indices.
   */
  keys(): IterableIterator<number> {
    return this.array.keys();
  }

  /**
   * Finds the first value matching the predicate.
   */
  find(predicate: (value: Readonly<T>, index: number) => boolean): Readonly<T> | undefined {
    for (let i = 0; i < this.array.length; i++) {
      const v = Object.freeze({ ...(this.array[i] as T) });
      if (predicate(v, i)) return v;
    }
    return undefined;
  }

  /**
   * Finds the index of the first value matching the predicate.
   */
  findIndex(predicate: (value: Readonly<T>, index: number) => boolean): number {
    for (let i = 0; i < this.array.length; i++) {
      if (predicate(Object.freeze({ ...(this.array[i] as T) }), i)) return i;
    }
    return -1;
  }

  /**
   * Returns true if any value matches the predicate.
   */
  some(predicate: (value: Readonly<T>, index: number) => boolean): boolean {
    return this.findIndex(predicate) !== -1;
  }

  /**
   * Returns true if all values match the predicate.
   */
  every(predicate: (value: Readonly<T>, index: number) => boolean): boolean {
    for (let i = 0; i < this.array.length; i++) {
      if (!predicate(Object.freeze({ ...(this.array[i] as T) }), i)) return false;
    }
    return true;
  }

  /**
   * Invokes the effect for each value in order.
   */
  forEach(effect: (value: Readonly<T>, index: number) => void): void {
    for (let i = 0; i < this.array.length; i++) effect(Object.freeze({ ...(this.array[i] as T) }), i);
  }

  /**
   * Maps values to a new readonly array.
   */
  map<U>(mapper: (value: Readonly<T>, index: number) => U): ReadonlyArray<U> {
    const out: U[] = new Array(this.array.length);
    for (let i = 0; i < this.array.length; i++) out[i] = mapper(Object.freeze({ ...(this.array[i] as T) }), i);
    return out;
  }

  /**
   * Flat maps values to a new readonly array.
   */
  flatMap<U>(mapper: (value: Readonly<T>, index: number) => U | U[]): ReadonlyArray<U> {
    const result: U[] = [];
    for (let i = 0; i < this.array.length; i++) {
      const v = mapper(Object.freeze({ ...(this.array[i] as T) }), i);
      if (Array.isArray(v)) result.push(...v);
      else result.push(v);
    }
    return result;
  }

  /**
   * Reduces values to a single value using the reducer and initial accumulator.
   */
  reduce<U>(reducer: (acc: U, value: Readonly<T>, index: number) => U, initial: U): U {
    let acc = initial;
    for (let i = 0; i < this.array.length; i++) acc = reducer(acc, Object.freeze({ ...(this.array[i] as T) }), i);
    return acc;
  }

  /**
   * Returns a readonly array of items matching the predicate.
   * Supports type predicates to preserve type narrowing through Readonly<> wrappers.
   * Note: When using a type predicate like `(item: Readonly<PaymentBreakdown>) => item is Readonly<StatementBreakdown>`,
   * TypeScript will correctly narrow `ReadonlyArray<Readonly<PaymentBreakdown>>` to `ReadonlyArray<Readonly<StatementBreakdown>>`.
   */
  filter<U extends T>(
    predicate: (value: Readonly<T>, index: number) => value is Readonly<U>,
  ): ReadonlyArray<Readonly<U>>;
  filter(predicate: (value: Readonly<T>, index: number) => boolean): ReadonlyArray<Readonly<T>>;
  filter<U extends T>(
    predicate: (value: Readonly<T>, index: number) => value is Readonly<U>,
  ): ReadonlyArray<Readonly<U>> {
    const out: Readonly<U>[] = [];
    for (let i = 0; i < this.array.length; i++) {
      const v = Object.freeze({ ...(this.array[i] as T) });
      if (predicate(v, i)) out.push(v);
    }
    return out;
  }

  /**
   * Returns the first item if present.
   */
  first(): Readonly<T> | undefined {
    if (!this.array.length) return undefined;
    return Object.freeze({ ...(this.array[0] as T) });
  }

  /**
   * Returns the last item if present.
   */
  last(): Readonly<T> | undefined {
    return this.array.length ? Object.freeze({ ...(this.array[this.array.length - 1] as T) }) : undefined;
  }

  /**
   * Returns true when the array is empty.
   */
  isEmpty(): boolean {
    return this.array.length === 0;
  }

  /**
   * Returns a shallow readonly copy of the underlying frozen array.
   */
  toReadonlyArray(): ReadonlyArray<Readonly<T>> {
    return this.array.map((item) => Object.freeze({ ...(item as T) }));
  }

  /**
   * Provides JSON serialization support.
   */
  toJSON(): ReadonlyArray<T> {
    // Return plain clones for serialization
    return this.array.map((item) => ({ ...(item as T) }));
  }
}

/**
 * Creates a Zod schema that validates an array and transforms it into an AppendOnlyArray.
 * This allows decoding JSON into AppendOnlyArray instances while maintaining immutability guarantees.
 *
 * @param itemSchema - The Zod schema for individual items in the array
 * @returns A Zod schema that validates an array and transforms it to AppendOnlyArray
 *
 * @example
 * ```ts
 * const PaymentBreakdownArray = zodAppendOnlyArray(PaymentBreakdown);
 * const result = PaymentBreakdownArray.parse([{ paymentId: '...', amount: 100 }]);
 * // result is an AppendOnlyArray<PaymentBreakdown>
 * ```
 */
export function zodAppendOnlyArray<T extends z.ZodTypeAny>(itemSchema: T) {
  return z
    .preprocess(
      (items: unknown) => (items instanceof AppendOnlyArray ? items.toMutableArray() : items),
      z.array(itemSchema),
    )
    .transform((items) => new AppendOnlyArray<z.infer<T> & object>(items as (z.infer<T> & object)[]));
}
